import React from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'
import ThemeInterface from '@/theme-interface'
import { addChecksumCalculation, removeChecksumCalculation, removePending, removeTodoUpload, toggleUploadsOverlay, updatePending } from '@/redux/ducks/document/upload'
import { setShallRefresh, LIST_TYPES } from '@/redux/ducks/lists'
import { useTheme } from '@mui/material'
import ProgressCircle from '@/modules/base/components/progress-indicators/progress-circle'
import { splitFilename } from '@/modules/_helpers/splitFilename'
import customDecodeUri from '@/utils/customDecodeUri'
import lang from '@/lang'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import StatusAction from '@/modules/layout/components/menuV2/upload-status/status-action'
import IconCancel from '@assets/images/icons/error2.svg'
import IconPause from '@assets/images/icons/pause.svg'
import IconResume from '@assets/images/icons/play.svg'
import IconRetry from '@assets/images/icons/update.svg'
import IconDone from '@assets/images/icons/check2.svg'
import IconOpen from '@assets/images/icons/search.svg'
import IconOpenFolder from '@assets/images/icons/document_folder.svg'
import FileService, { calculateChecksum, CHUNK_SIZE, SINGLE_UPLOAD_MAX_SIZE } from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { removeUploadFromList, setFolder } from '@/redux/ducks/document/list'
import { findFolderById } from '@/modules/file/_helpers/findFoldersHelpers'
import { openDocumentDrawer } from '@/redux/ducks/document/documentDrawerHelpers'
import { fetchFolders, fetchListAction } from '@/redux/ducks/lists/requests/documentList'
import { getAppState, useAppSelector } from '@/redux/store'
import DocumentsUploadTrigger from '@/modules/document/components/upload/documents-upload-trigger'
import { v4 as uuidv4 } from 'uuid'
import { QUERY_TYPES } from '@/redux/ducks/lists'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { refreshList } from '@/redux/ducks/lists/requests/todoList'
// FLAG:useDocuAi — surface the real failure reason instead of the constant label.
import { isUploadHardeningEnabled } from '@/redux/ducks/document/_helpers/uploadHardening'
import { getUploadErrorText, isMissingUploadError } from '@/redux/ducks/document/_helpers/uploadErrorMessage'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    display: 'flex',
    '&:hover': {
      background: theme.backgroundColor,
    },
    padding: 4,
    alignItems: 'center',
    gap: 10,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: '1 1 0',
    overflow: 'hidden',
    lineHeight: 1.3,
    marginTop: 3,
    marginRight: 10,
  },
  label: {
    maxWidth: '100%',
    overflow: 'hidden',
    color: theme.black,
    display: 'flex',
  },
  fileName: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  suffix: {
    marginRight: 10,
  },
  progress: {
    color: theme.grey1,
    margin: CssHelper.px(3, 8, 3, 5),
  },
  subTitle: {
    color: theme.grey1,
    flex: '1 1 0',
  },
  error: {
    color: theme.error,
    flex: '1 1 0',
  },
  // FLAG:useDocuAi — the detailed variant. A server message can be far longer than the constant, so
  // it gets its own class (clamped, wrapping) rather than widening the shared `error` style, which
  // the flag-OFF branch keeps rendering unchanged.
  errorDetail: {
    color: theme.error,
    flex: '1 1 0',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  fileInput: {
    cursor: 'pointer',
    '& input[type=\'file\']': {
      cursor: 'pointer',
      fontSize: 0, // needed to make cursor pointer work. don't ask why
    },
  },
}))

interface PendingUploadProps {
  uploadToken: string,
}

const PendingUploadStatus = (props: PendingUploadProps) => {
  const dispatch = useDispatch()

  const theme: ThemeInterface = useTheme()
  const classes = useStyles()
  const { uploadToken } = props
  const upload = useAppSelector((state) => state.document.upload.pending).find((item) => uploadToken === item.uploadToken)
  // The row can disappear between the parent's render and this one — closing the uploads overlay
  // filters finished rows out of state. Destructuring undefined here crashed the whole flyout.
  const {
    fileName,
    maxPartNumber,
    totalChunks,
    error,
    running,
    pausing,
    file,
    blocked,
    progress,
    finished,
    completing,
    attempt,
    maxAttempts,
  } = upload ?? {}

  const { name, suffix } = splitFilename(customDecodeUri(fileName ?? ''))

  const color = error
    ? theme.error
    : finished
      ? theme.green
      : running
        ? theme.primary
        : theme.grey2

  const onCancel = () => {
    const cancelled = FileService.cancelUpload({ uploadToken }).then(() => {
      dispatch(removePending(upload))
      dispatch(removeUploadFromList(uploadToken))

      if (upload?.todoId) {
        dispatch(removeTodoUpload(upload.todoId))
        dispatch(refreshList)
      }

      dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
      dispatch(fetchFolders)
    })

    // FLAG:useDocuAi — the row must also go when the server says the upload no longer exists.
    // cancel-upload answers NOT_FOUND once the File row is gone (the user deleted the document from
    // the grid first), and because the removal above lives only in `.then`, the ✕ then did nothing
    // at all: the row was stuck in the flyout until a page reload. Any OTHER failure is re-thrown so
    // it stays as loud as it was — we only forgive the one case where the row is provably a ghost.
    // Flag-OFF the promise carries no catch, exactly as before.
    if (!isUploadHardeningEnabled()) return
    cancelled.catch((error) => {
      if (!isMissingUploadError(error)) throw error
      dispatch(removePending(upload))
      dispatch(removeUploadFromList(uploadToken))
      if (upload?.todoId) {
        dispatch(removeTodoUpload(upload.todoId))
      }
    })
  }

  const onPause = () => {
    dispatch(updatePending({ ...upload, pausing: true, blocked: true }))
  }

  const onResume = () => {
    if (file.size > SINGLE_UPLOAD_MAX_SIZE) {
      FileService.startMultipartUpload({ ...upload, file: upload.file })
    } else {
      FileService.startSingleUpload({ ...upload, file: upload.file })
    }
  }

  const onRetry = () => {
    // FLAG:useDocuAi — if the bytes already reached the DMS (minioId present) only complete-upload
    // can have failed, so re-complete instead of re-uploading. Re-running the upload would create a
    // second DMS version of the same document. Both hardened chains now record minioId, so this
    // covers Documents as well as receipts; a flag-OFF row never carries one and keeps the original
    // behaviour.
    if (upload.minioId) {
      FileService.retryCompleteUpload(upload)
      return
    }
    if (file.size > SINGLE_UPLOAD_MAX_SIZE) {
      FileService.startMultipartUpload({ ...upload, file: upload.file })
    } else {
      FileService.startSingleUpload({ ...upload, file: upload.file })
    }
  }

  const onSubmitted = async([{ file }]) => {
    const { name, size } = file
    const uuid = uuidv4()
    const totalChunks = Math.ceil(size / CHUNK_SIZE)

    dispatch(addChecksumCalculation({ uuid, totalChunks, currentChunk: 0, fileName: name }))
    const { checksum } = await calculateChecksum(uuid, file, totalChunks)

    if (checksum !== upload.checksum) {
      dispatch(removeChecksumCalculation(uuid))
      dispatch(toggleUploadsOverlay(false))
      toast.showToast({ type: 'error', message: lang.documents.upload.errors.wrongFile })
      return
    }

    dispatch(updatePending({ ...upload, file }))
    dispatch(removeChecksumCalculation(uuid))
    setTimeout(() => {
      if (file.size > SINGLE_UPLOAD_MAX_SIZE) {
        FileService.startMultipartUpload({ ...upload, file })
      } else {
        FileService.startSingleUpload({ ...upload, file })
      }
    }, 10)
  }

  const openFolder = () => {
    dispatch(toggleUploadsOverlay(false))

    if (!window.location.href.includes('/documents')) {
      navigate(`documents?folderId=${upload.folderId}`)
    } else {
      const folder = findFolderById(getAppState().document.list.folders, upload.folderId)
      if (folder) {
        setTimeout(() => {
          setFolder(folder)
        }, 50)
      }
    }
  }

  const onOpenFolder = () => {
    openFolder()
  }

  const onOpen = () => {
    openFolder()
    navigate(`documents?documentId=${upload.fileId}`)
  }

  const pending = useAppSelector((state) => state.document.upload.pending)
  const calculating = useAppSelector((state) => state.document.upload.calculating)
  const disabled = pending.filter(({ running, error }) => !!running && !error).length || calculating.filter(({ error }) => !error).length
  const disabledTooltip = lang.documents.upload.uploadsPending

  // Placed after every hook so the hook order stays stable when the row vanishes mid-render.
  if (!upload) return null

  const retryAction = file
    ? <StatusAction color={theme.borderOutlineColor} disabled={!!blocked} onClick={onRetry} icon={IconRetry} tooltip={lang.documents.uploading.tooltips.retry} invert size='11px' />
    : (<DocumentsUploadTrigger disabled={!!disabled} className={classes.fileInput} multiple={false} allowedExtensions={[splitFilename(upload.fileName).suffix]} onSubmit={onSubmitted}>
      <StatusAction disabled={!!disabled} color={theme.borderOutlineColor} icon={IconRetry} tooltip={disabled ? disabledTooltip : lang.documents.uploading.tooltips.retry} size='11px' />
    </DocumentsUploadTrigger>)

  const resumeAction = file
    ? <StatusAction color={theme.borderOutlineColor} disabled={!!blocked} onClick={onResume} icon={IconResume} tooltip={lang.documents.uploading.tooltips.resume} invert fixIcon />
    : (<DocumentsUploadTrigger disabled={!!disabled} className={classes.fileInput} multiple={false} allowedExtensions={[splitFilename(upload.fileName).suffix]} onSubmit={onSubmitted}>
      <StatusAction disabled={!!disabled} color={theme.borderOutlineColor} icon={IconResume} tooltip={disabled ? disabledTooltip : lang.documents.uploading.tooltips.resume} invert fixIcon />
    </DocumentsUploadTrigger>)

  const finishedAction = <StatusAction color={theme.green} onClick={() => dispatch(removePending(upload))} tooltip={lang.documents.uploading.tooltips.remove} icon={IconDone} invert />
  const runningAction = totalChunks > 1 ? <StatusAction color={theme.borderOutlineColor} onClick={onPause} icon={IconPause} tooltip={lang.documents.uploading.tooltips.pause} /> : null
  const openFolderAction = <StatusAction color={theme.black} onClick={onOpenFolder} icon={IconOpenFolder} tooltip={lang.documents.uploading.tooltips.openFolder} noBorder />
  const openFileAction = <StatusAction color={theme.black} onClick={onOpen} icon={IconOpen} tooltip={lang.documents.uploading.tooltips.open} noBorder />
  const cancelAction = <StatusAction color={theme.black} onClick={onCancel} icon={IconCancel} tooltip={lang.documents.uploading.tooltips.cancel} />

  // FLAG:useDocuAi — show WHY the upload failed. The row has carried the server's message all along
  // (onUploadError writes it), but the flyout rendered the constant label for every failure, so a
  // precise answer — a rejected file type, a quota refusal, an http status — reached the user as an
  // unqualified "Fehler beim Hochladen" and was unrecoverable afterwards. Flag-OFF renders the
  // identical constant span it always did.
  const errorLabel = isUploadHardeningEnabled()
    ? (() => {
      const errorText = getUploadErrorText(error, lang.documents.uploading.states.error)
      return <span className={classes.errorDetail} title={errorText}>{errorText}</span>
    })()
    : <span className={classes.error}>{lang.documents.uploading.states.error}</span>

  // FLAG:useDocuAi — `completing` appears only on receipt rows; the retry counter now also appears
  // on Documents rows (both hardened chains report their attempts). A flag-OFF row leaves both
  // undefined and falls through to the original chain unchanged.
  const subTitle = finished
    ? lang.documents.uploading.states.done
    : completing
      ? lang.documents.uploading.states.completing
      : attempt > 1
        ? lang.documents.uploading.states.retrying(attempt, maxAttempts)
        : pausing
          ? lang.documents.uploading.states.pausing
          : running
            ? lang.documents.uploading.states.running
            : lang.documents.uploading.states.paused

  return (
    <div className={classes.content}>
      <ProgressCircle
        size={36}
        customClass={classes.progress}
        borderColor={color}
        borderWidth={2}
        fontSize={'11px'}
        progress={progress ? Math.floor(progress) : Math.round(((maxPartNumber ?? 0) / totalChunks) * 100)}
      />
      <span className={classes.header}>
        <span className={classes.label}>
          <span className={classes.fileName}>{name}</span>
          <span className={classes.suffix}>{suffix}</span>
        </span>
        {error
          ? errorLabel
          : <span className={classes.subTitle}>{subTitle}</span>}
      </span>
      {error ? retryAction : null }
      {!finished && !error ? (running ? runningAction : resumeAction) : null}
      {!finished ? cancelAction : null}
      {finished ? finishedAction : null}
      {finished ? openFolderAction : null}
      {finished ? openFileAction : null}
    </div>
  )
}

export default PendingUploadStatus
